import { useMutation, useQuery } from '@apollo/client';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Button, Checkbox, Grid, InputBase, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-router';
import { type FormikHelpers, useFormik } from 'formik';
import React, { type ChangeEvent, useEffect, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { AddButton, AddUsersModal, Card, Table, Title } from 'src/Administration/Common';
import type { Profile as AddUsersProfile } from 'src/Administration/Common/AddUsersModal/AddUsersModal';
import allMediagroups from 'src/api/permission/allMediaGroups';
import deleteMediaPermission from 'src/api/permission/deleteMediaPermission';
import newMediaPermission from 'src/api/permission/newMediaPermission';
import singlePermissionGroup from 'src/api/permission/singlePermissionGroup';
import updateMediaPermission from 'src/api/permission/updateMediaPermission';
import updatePermissionGroup from 'src/api/permission/updatePermissionGroup';
import { useCustomToast } from 'src/hooks/menu';
import * as Yup from 'yup';
import { AdminTextInput } from '../Common/input';
import styles from './EditPermisionGroupPageStyles';

const GRID_CONTAINER_SPACING = 4;

interface HeadCell {
  id: string;
  numeric: boolean;
  disablePadding: boolean;
  label: string;
}

interface MediaGroup {
  id: string;
  name: string;
}

interface MediaGroupNode {
  node: MediaGroup;
}

interface AllMediaGroupsData {
  allMediaGroups: {
    edges: MediaGroupNode[];
  };
}

type Profile = AddUsersProfile;

interface MediaPermission {
  id?: string;
  canEdit: boolean;
  canDelete: boolean;
  canDownload: boolean;
  canShare: boolean;
  hires: boolean;
  profile: Profile | null;
}

interface MediaPermissionNode {
  node: MediaPermission;
}

interface PermissionGroupData {
  permissionGroup: {
    id: string;
    name: string;
    description: string;
    mediaGroups: {
      edges: MediaGroupNode[];
    };
    mediapermissionSet: {
      edges: MediaPermissionNode[];
    };
  };
}

interface TableCellValue {
  value: React.ReactNode;
  display: boolean;
}

type TableRow = TableCellValue[];

interface FormValues {
  name: string;
  description: string;
}

const headCellsUsersList: HeadCell[] = [
  { id: 'Username', numeric: true, disablePadding: false, label: 'Username' },
  { id: 'canEdit', numeric: true, disablePadding: false, label: 'Edit' },
  { id: 'canShare', numeric: true, disablePadding: false, label: 'Share' },
  { id: 'canDownload', numeric: true, disablePadding: false, label: 'Download' },
  { id: 'canDelete', numeric: true, disablePadding: false, label: 'Delete' },
  { id: 'hires', numeric: true, disablePadding: false, label: 'Request Hires' },
  { id: 'description', numeric: true, disablePadding: false, label: '' },
];

const generateCheckBoxes = (
  onChangeCan: (
    fieldIndex: number,
    user: MediaPermission,
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => void,
  a: MediaPermission,
): TableCellValue[] => {
  return Array(5)
    .fill(undefined)
    .map((_, e) => ({
      display: true,
      value: (
        <Checkbox
          key={headCellsUsersList[e + 1].id}
          sx={{ color: 'text.primary', '&.Mui-checked': { color: 'primary.main' } }}
          defaultChecked={a[headCellsUsersList[e + 1].id as keyof MediaPermission] as boolean}
          onChange={(event) => onChangeCan(e, a, event, event.target.checked)}
        />
      ),
    }));
};

function convertUsersRows(
  data: MediaPermission[],
  onChangeCan: (
    fieldIndex: number,
    user: MediaPermission,
    event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => void,
  onRemoveUser: (item: MediaPermission) => void,
): TableRow[] {
  const classes = styles();
  return data.map((a) => [
    { display: true, value: a.profile?.user.username ?? '' },
    ...generateCheckBoxes(onChangeCan, a),
    {
      display: true,
      value: (
        <DeleteIcon
          className={classes.deleteIcon}
          key={a.id ?? a.profile?.id}
          color="error"
          onClick={() => onRemoveUser(a)}
        />
      ),
    },
  ]);
}

interface EditPermissionPageComponentProps {
  id: string | null;
}

export default function EditPermissionPageComponent({
  id,
}: EditPermissionPageComponentProps): React.ReactElement | null {
  const {
    data: permissionGroup,
    loading,
    error,
  } = useQuery<PermissionGroupData>(singlePermissionGroup, {
    variables: { id },
    fetchPolicy: 'network-only',
    skip: !id,
  });

  // Debug: log render state
  console.log('[EditPermissionPageComponent] Render:', {
    id,
    loading,
    error: error?.message,
    hasData: !!permissionGroup,
  });

  if (!id) {
    console.warn('[EditPermissionPageComponent] No id provided, returning null');
    return null;
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
          flexDirection: 'column',
        }}
      >
        <Typography color="error">Error loading permission group</Typography>
        <Typography color="error" variant="body2">
          {error.message}
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Loading permission group...</Typography>
      </Box>
    );
  }

  if (!permissionGroup) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Waiting for data...</Typography>
      </Box>
    );
  }

  if (!permissionGroup.permissionGroup) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
          flexDirection: 'column',
        }}
      >
        <Typography color="error">Permission group not found</Typography>
        <Typography color="textSecondary" variant="body2">
          The requested permission group does not exist or you do not have access to it.
        </Typography>
      </Box>
    );
  }

  const mediapermissionSet = permissionGroup.permissionGroup.mediapermissionSet.edges
    .map((a) => a.node)
    .filter(({ profile }) => !!profile);

  return (
    <EditPermissionGroupPage
      permissionGroupData={permissionGroup.permissionGroup}
      mediapermissionSet={mediapermissionSet}
      id={id}
    />
  );
}

interface EditPermissionGroupPageProps {
  permissionGroupData: PermissionGroupData['permissionGroup'];
  id: string;
  mediapermissionSet: MediaPermission[];
}

function EditPermissionGroupPage({
  permissionGroupData,
  id: permissionGroupId,
  mediapermissionSet,
}: EditPermissionGroupPageProps): React.ReactElement {
  const classes = styles();
  const defaultSelectedMediaGroups = permissionGroupData.mediaGroups.edges.map((a) => a.node);
  const [selectedMediaGroups, setSelectedMediaGroups] = useState<MediaGroup[]>(defaultSelectedMediaGroups);
  const [removedUsersFromPermission, setRemovedUsersFromPermission] = useState<MediaPermission[]>([]);
  const [usersList, setUsersList] = useState<MediaPermission[]>(mediapermissionSet);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [updateNewPermission] = useMutation(updatePermissionGroup);
  const [createMediaPermission] = useMutation(newMediaPermission);
  const [editMediaPermission] = useMutation(updateMediaPermission);
  const [deleteMediaPermissionReq] = useMutation(deleteMediaPermission);
  const navigate = useNavigate();
  const { toastr } = useCustomToast();

  const formik = useFormik<FormValues>({
    initialValues: {
      name: permissionGroupData.name,
      description: permissionGroupData.description,
    },
    validationSchema: Yup.object({
      name: Yup.string().required('This field is required'),
    }),
    onSubmit: async (values: FormValues, submitProps: FormikHelpers<FormValues>) => {
      const { description } = values;

      updateNewPermission({
        variables: {
          id: permissionGroupId,
          description,
          mediaGroups: selectedMediaGroups.map(({ id }) => ({ id })),
        },
        refetchQueries: [singlePermissionGroup],
      })
        .then(() => {
          for (const { id } of removedUsersFromPermission) {
            if (id) {
              deleteMediaPermissionReq({
                variables: { mediaPermissionId: id },
                refetchQueries: [singlePermissionGroup],
              });
            }
          }

          for (const user of usersList) {
            const {
              canDelete = false,
              canDownload = false,
              canEdit = false,
              canShare = false,
              hires = false,
              id,
              profile,
            } = user;
            if (id) {
              editMediaPermission({
                variables: {
                  mediaPermissionId: id,
                  canDelete,
                  canDownload,
                  canEdit,
                  canShare,
                  hires,
                },
                refetchQueries: [singlePermissionGroup],
              });
            } else if (profile) {
              createMediaPermission({
                variables: {
                  profileId: profile.id,
                  permissionsGroupId: permissionGroupId,
                  canDelete,
                  canDownload,
                  canEdit,
                  canShare,
                  hires,
                },
                refetchQueries: [singlePermissionGroup],
              });
            }
          }

          toastr.success('Permission Group updated successfully');
          submitProps.resetForm({ values });
        })
        .catch((e: Error) => {
          toastr.error(e.message);
        });
    },
  });

  const onClickCancel = (): void => {
    navigate({ to: '/systemadmin/groups' });
  };

  const onAddUsers = (list: Profile[]): void => {
    setUsersList((e) => [
      ...e,
      ...list.map((profile) => ({
        profile,
        canEdit: false,
        canDelete: false,
        canDownload: false,
        canShare: false,
        hires: false,
      })),
    ]);
  };

  const isUserAlreadyAdded = (profileId: string): boolean => usersList.some((u) => u.profile?.id === profileId);

  const onChangeCan = (
    fieldIndex: number,
    user: MediaPermission,
    _event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ): void => {
    const _field = headCellsUsersList[fieldIndex + 1].id as keyof MediaPermission;
    setUsersList((e) => e.map((v) => (v.id === user.id ? { ...v, [_field]: checked } : v)));
  };

  const addToSelectedGroups = (e: MediaGroup): void => setSelectedMediaGroups((v) => [...v, e]);
  const removeFromSelectedGroups = (e: MediaGroup): void =>
    setSelectedMediaGroups((v) => v.filter(({ id }) => id !== e.id));

  const onRemoveUser = (item: MediaPermission): void => {
    if (item.id) setRemovedUsersFromPermission((e) => [...e, item]);
    if (item.id) setUsersList((v) => v.filter((e) => e.id !== item.id));
    else setUsersList((v) => v.filter((e) => e.profile?.id !== item.profile?.id));
  };

  useEffect(() => {
    if (formik.dirty || selectedMediaGroups.length !== defaultSelectedMediaGroups.length) {
      window.onbeforeunload = (): boolean => true;
    } else {
      window.onbeforeunload = null;
    }
  }, [formik.dirty, selectedMediaGroups.length, defaultSelectedMediaGroups.length]);

  return (
    <Box sx={{ width: '95%', margin: '0 auto', marginTop: '30px' }}>
      <AddUsersModal
        onAddUsers={onAddUsers}
        isUserAlreadyAdded={isUserAlreadyAdded}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />
      <Card>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={GRID_CONTAINER_SPACING}>
            <Grid item xs={12}>
              <Title>Edit Permission Group</Title>
            </Grid>
          </Grid>

          <Grid container spacing={GRID_CONTAINER_SPACING} style={{ marginBottom: '30px' }}>
            <Grid item xs={6}>
              <div className={classes.cardTitle}>Group Name</div>
              <AdminTextInput label={'Group Name'} disabled {...formik.getFieldProps('name')} />
            </Grid>
            <Grid item xs={6}>
              <div className={classes.cardTitle}>Description</div>
              <AdminTextInput label={'Description'} {...formik.getFieldProps('description')} />
            </Grid>
          </Grid>

          <Grid container spacing={GRID_CONTAINER_SPACING} style={{ marginBottom: '30px' }}>
            <Grid item xs={6}>
              <div className={classes.cardTitle}>Available Media Groups</div>
              <PermissionListSearch onSelect={addToSelectedGroups} selectedPermissions={selectedMediaGroups} />
            </Grid>
            <Grid item xs={6}>
              <div className={classes.cardTitle}>Selected Media Groups</div>
              <SelectedPermission onRemove={removeFromSelectedGroups} selectedPermissions={selectedMediaGroups} />
            </Grid>
          </Grid>

          <Grid container spacing={GRID_CONTAINER_SPACING}>
            <Grid className={classes.cancelSaveButtons} item xs={12}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={Object.keys(formik.errors).length > 0}
                style={{ marginRight: '20px' }}
              >
                Save
              </Button>
              <Button onClick={onClickCancel} color="secondary">
                Cancel
              </Button>
            </Grid>
          </Grid>
          <Grid
            className={classes.groupUsersContainer}
            container
            spacing={GRID_CONTAINER_SPACING}
            style={{ marginBottom: '30px' }}
          >
            <Grid className={classes.headerContainer} item xs={12}>
              <Title>Group Users</Title>
              <AddButton onClick={() => setIsModalOpen(true)}>Add User</AddButton>
            </Grid>
            <Grid item xs={12}>
              <Table
                minTableHeight={800}
                headCells={headCellsUsersList}
                rows={usersList ? convertUsersRows(usersList, onChangeCan, onRemoveUser) : []}
              />
            </Grid>
          </Grid>
        </form>
      </Card>
    </Box>
  );
}

interface SelectedPermissionProps {
  selectedPermissions: MediaGroup[];
  onRemove: (node: MediaGroup) => void;
}

const SelectedPermission = ({ selectedPermissions, onRemove }: SelectedPermissionProps): React.ReactElement => {
  const classes = styles();
  const theme = useTheme();

  const onClickGroup = (node: MediaGroup): void => {
    onRemove(node);
  };

  const [{ isOver: isOver1 }, dropTarget1] = useDrop({
    accept: 'EDIT_PERMISSION_ITEM',
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <div
      ref={(node) => {
        dropTarget1(node);
      }}
      className={classes.permissionContainer}
      style={{ padding: '30px 15px', backgroundColor: isOver1 ? theme.palette.grey[400] : theme.palette.grey[500] }}
    >
      <div className={classes.permissionListContainer}>
        {selectedPermissions.map((a) => (
          <div key={a.id} className={classes.addedContentItem}>
            <div>{a.name}</div>
            <button
              type="button"
              onClick={() => onClickGroup(a)}
              style={{
                marginRight: '10px',
                display: 'flex',
                alignItems: 'center',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
              }}
            >
              <CancelIcon className={classes.cancelIcon} fontSize="small" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

interface PermissionListSearchProps {
  selectedPermissions: MediaGroup[];
  onSelect: (node: MediaGroup) => void;
}

const PermissionListSearch = ({ selectedPermissions, onSelect }: PermissionListSearchProps): React.ReactElement => {
  const classes = styles();
  const [searchVal, setSearchVal] = useState('');
  const { data: groupsList } = useQuery<AllMediaGroupsData>(allMediagroups, {
    fetchPolicy: 'cache-and-network',
  });

  const onChangeSearch = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setSearchVal(e.target.value);
  };

  const handleDrop1 = (node: MediaGroup): void => {
    onSelect(node);
  };

  return (
    <div className={classes.permissionContainer}>
      <div className={classes.permissionSearchBox}>
        <InputBase
          className={classes.searchInput}
          placeholder={'Quick Search'}
          value={searchVal}
          onChange={onChangeSearch}
        />
      </div>
      <div className={classes.permissionListContainer}>
        {groupsList?.allMediaGroups?.edges
          .filter((a) => !selectedPermissions.find((b) => b.id === a.node.id))
          .filter((a) => a.node.name.toUpperCase().includes(searchVal.toUpperCase()))
          .map((a, index) => (
            <PermissionListSearchItem
              onSelect={onSelect}
              a={a}
              key={a.node.id}
              index={index}
              handleDrop1={handleDrop1}
            />
          ))}
      </div>
    </div>
  );
};

interface PermissionListSearchItemProps {
  onSelect: (node: MediaGroup) => void;
  a: MediaGroupNode;
  index: number;
  handleDrop1: (node: MediaGroup) => void;
}

const PermissionListSearchItem = ({
  onSelect,
  a,
  index,
  handleDrop1,
}: PermissionListSearchItemProps): React.ReactElement => {
  const classes = styles();

  const onClickGroup = (node: MediaGroup): void => {
    onSelect(node);
  };

  const id = a.node.id;
  const [{ isDragging: isDragging1 }, dragTarget1] = useDrag({
    type: 'EDIT_PERMISSION_ITEM',
    item: { type: 'EDIT_PERMISSION_ITEM', id, index },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
    end: (_dragItem, monitor) => {
      if (monitor.didDrop()) {
        handleDrop1(a.node);
      }
    },
  });

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: Drag-and-drop item also supports click selection; complex interaction pattern
    // biome-ignore lint/a11y/useKeyWithClickEvents: Drag-and-drop item also supports click selection; complex interaction pattern
    <div
      ref={(node) => {
        dragTarget1(node);
      }}
      style={{ opacity: isDragging1 ? 0.5 : 1 }}
      className={classes.permissionListItem}
      onClick={() => onClickGroup(a.node)}
    >
      {a.node.name}
    </div>
  );
};
